export interface CloudinaryUploadResponse {
  url: string;
  public_id: string;
  secure_url: string;
}

export interface CloudinaryError {
  message: string;
  error?: unknown;
}

export const uploadQuestionImageToCloudinary = async (
  file: File
): Promise<CloudinaryUploadResponse> => {
  const cloudName = import.meta.env.CLOUDINARY_CLOUD_NAME;
  const uploadPreset = import.meta.env.CLOUDINARY_UPLOAD_PRESET;

  if (!cloudName || !uploadPreset) {
    throw new Error(
      'Cloudinary configuration is missing. Please check your environment variables.'
    );
  }

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      'Invalid file type. Please upload a JPEG, PNG, or WebP image.'
    );
  }

  // Validate file size (max 5MB for questions)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    throw new Error(
      'File size too large. Please upload an image smaller than 5MB.'
    );
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);
  formData.append('folder', 'lingua-craft/questions');

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to upload image');
    }

    const data = await response.json();

    return {
      url: data.secure_url,
      public_id: data.public_id,
      secure_url: data.secure_url,
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error('Failed to upload image. Please try again.');
  }
};

export const generateQuestionImageUrl = (
  publicId: string,
  options?: {
    width?: number;
    height?: number;
    crop?: string;
    quality?: string;
    format?: string;
  }
): string => {
  const cloudName = import.meta.env.CLOUDINARY_CLOUD_NAME;

  if (!cloudName || !publicId) {
    return '';
  }

  let transformations = '';

  if (options) {
    const transformParts = [];

    if (options.width) transformParts.push(`w_${options.width}`);
    if (options.height) transformParts.push(`h_${options.height}`);
    if (options.crop) transformParts.push(`c_${options.crop}`);
    if (options.quality) transformParts.push(`q_${options.quality}`);
    if (options.format) transformParts.push(`f_${options.format}`);

    if (transformParts.length > 0) {
      transformations = `/${transformParts.join(',')}`;
    }
  }

  return `https://res.cloudinary.com/${cloudName}/image/upload${transformations}/${publicId}`;
};
